import React, { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api, { apiError } from "../lib/api";
import AuthShell from "./AuthShell";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const isAdmin = useLocation().pathname.startsWith("/admin");
  const loginPath = isAdmin ? "/admin/login" : "/login";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const field = "w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-0 transition-colors duration-200";
  const eyeBtn = "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37] transition-colors duration-200";

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      toast.error("Password must be at least 8 characters and include a letter and a number.");
      return;
    }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell testid="reset-page" title={isAdmin ? "Admin Password Reset" : "Reset Password"} subtitle="This link is invalid.">
        <p className="text-gray-400 text-sm">The reset link is missing or malformed. <Link to="/forgot-password" className="text-[#D4AF37]">Request a new one</Link>.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell testid="reset-page" title={isAdmin ? "Set a new admin password" : "Set a new password"} subtitle="Choose a strong new password for your account.">
      {done ? (
        <div data-testid="reset-success" className="bg-[#141414] border border-[#D4AF37]/30 rounded-2xl p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <p className="text-gray-300">Your password has been updated. You can now sign in.</p>
          <button data-testid="reset-goto-login" onClick={() => navigate(loginPath)} className="mt-5 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium">Go to Sign In</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <input data-testid="reset-password" type={showPw ? "text" : "password"} required minLength={8} placeholder="New password (min 8 chars, letter + number)" value={password} onChange={(e) => setPassword(e.target.value)} className={field + " pr-12"} />
            <button type="button" data-testid="reset-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className={eyeBtn}>{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
          </div>
          <div className="relative">
            <input data-testid="reset-confirm" type={showConfirm ? "text" : "password"} required placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field + " pr-12"} />
            <button type="button" data-testid="reset-confirm-toggle" onClick={() => setShowConfirm((v) => !v)} aria-label={showConfirm ? "Hide password" : "Show password"} className={eyeBtn}>{showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
          </div>
          <button data-testid="reset-submit" disabled={loading} className="w-full py-3.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
